import { VoiceTextarea } from '../components/VoiceTextarea';
// src/modules/BodyMap.tsx
import React, { useState, useRef, useEffect } from 'react';

export type ViewType = 'front' | 'back' | 'left' | 'right';
export type Gender = 'male' | 'female';

export interface BodyPart {
  id: string;
  name: string;
  system?: string;
}

export interface Annotation {
  id: string;
  x: number;      // percentage (0-100) relative to image width
  y: number;      // percentage (0-100) relative to image height
  note: string;
  severity: 'mild' | 'moderate' | 'severe';
  files?: string[]; // base64 or URLs
  view: ViewType;
  gender: Gender;
}

interface BodyMapProps {
  selectedParts?: BodyPart[];
  onPartSelect?: (part: BodyPart) => void;
  onPartDeselect?: (part: BodyPart) => void;
  annotations?: Annotation[];        // passed from parent
  onAnnotationsChange?: (annotations: Annotation[]) => void; // update parent
  // For "new" mode, we need to know if we are creating a new record
  // We'll always show the body map, so we don't need to hide it.
}

// ─── IMAGE URLS ──────────────────────────────────────────────
// Make sure you've copied .webp files to public/body/
const IMAGES = {
  male: {
    front: '/body/male-front.webp',
    back: '/body/male-back.webp',
    left: '/body/male-front.webp',   // placeholder – replace with side image
    right: '/body/male-front.webp',
  },
  female: {
    front: '/body/female-front.webp',
    back: '/body/female-back.webp',
    left: '/body/female-front.webp',
    right: '/body/female-front.webp',
  },
};

// ─── PREDEFINED REGIONS (broad areas) ──────────────────────
const REGIONS = {
  male: [
    { id: 'head', label: 'Head', shape: 'circle' as const, coords: '250,130,60' },
    { id: 'chest', label: 'Chest', shape: 'rect' as const, coords: '190,210,120,90' },
    { id: 'abdomen', label: 'Abdomen', shape: 'rect' as const, coords: '190,300,120,90' },
    { id: 'l_arm', label: 'Left Arm', shape: 'rect' as const, coords: '130,220,40,110' },
    { id: 'r_arm', label: 'Right Arm', shape: 'rect' as const, coords: '330,220,40,110' },
    { id: 'l_leg', label: 'Left Leg', shape: 'rect' as const, coords: '190,400,40,160' },
    { id: 'r_leg', label: 'Right Leg', shape: 'rect' as const, coords: '270,400,40,160' },
  ],
  female: [
    { id: 'head', label: 'Head', shape: 'circle', coords: '250,130,60' },
    { id: 'chest', label: 'Chest', shape: 'rect', coords: '190,210,120,90' },
    { id: 'abdomen', label: 'Abdomen', shape: 'rect', coords: '190,300,120,90' },
    { id: 'l_arm', label: 'Left Arm', shape: 'rect', coords: '130,220,40,110' },
    { id: 'r_arm', label: 'Right Arm', shape: 'rect', coords: '330,220,40,110' },
    { id: 'l_leg', label: 'Left Leg', shape: 'rect', coords: '190,400,40,160' },
    { id: 'r_leg', label: 'Right Leg', shape: 'rect', coords: '270,400,40,160' },
  ],
};

// ─── MAIN COMPONENT ──────────────────────────────────────────
const BodyMap: React.FC<BodyMapProps> = ({
  selectedParts = [],
  onPartSelect,
  onPartDeselect,
  annotations = [],
  onAnnotationsChange,
}) => {
  const [gender, setGender] = useState<Gender>('male');
  const [view, setView] = useState<ViewType>('front');
  const [isAddingAnnotation, setIsAddingAnnotation] = useState(false);
  const [tempAnnotation, setTempAnnotation] = useState<{ x: number; y: number } | null>(null);
  const [noteText, setNoteText] = useState('');
  const [severity, setSeverity] = useState<'mild' | 'moderate' | 'severe'>('mild');
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);

  const imageUrl = IMAGES[gender][view];
  const regions = REGIONS[gender] || [];

  const isSelected = (id: string) => selectedParts.some(p => p.id === id);

  const handleRegionClick = (region: any) => {
    const part: BodyPart = { id: region.id, name: region.label };
    if (isSelected(region.id)) {
      onPartDeselect?.(part);
    } else {
      onPartSelect?.(part);
    }
  };

  // Handle click on image to drop a pin
  const handleImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!imageContainerRef.current) return;
    const rect = imageContainerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setTempAnnotation({ x, y });
    setIsAddingAnnotation(true);
  };

  const handleSaveAnnotation = () => {
    if (!tempAnnotation) return;
    const newAnnotation: Annotation = {
      id: Date.now().toString(36) + Math.random().toString(36).substr(2, 5),
      x: tempAnnotation.x,
      y: tempAnnotation.y,
      note: noteText,
      severity,
      view,
      gender,
      files: files.map(f => URL.createObjectURL(f)), // store as object URLs for preview
    };
    const updated = [...annotations, newAnnotation];
    onAnnotationsChange?.(updated);
    // Reset form
    setIsAddingAnnotation(false);
    setTempAnnotation(null);
    setNoteText('');
    setSeverity('mild');
    setFiles([]);
  };

  const handleCancelAnnotation = () => {
    setIsAddingAnnotation(false);
    setTempAnnotation(null);
    setNoteText('');
    setSeverity('mild');
    setFiles([]);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(Array.from(e.target.files));
    }
  };

  const handleDeleteAnnotation = (id: string) => {
    const updated = annotations.filter(a => a.id !== id);
    onAnnotationsChange?.(updated);
  };

  return (
    <div style={{ padding: '16px', background: '#F8FAFC', borderRadius: '12px' }}>
      {/* Controls */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div>
          <span style={{ fontWeight: 'bold', marginRight: '8px' }}>Gender:</span>
          <button onClick={() => setGender('male')} style={{ padding: '4px 12px', background: gender === 'male' ? '#2B6CB0' : '#E2E8F0', color: gender === 'male' ? '#FFF' : '#000', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '4px' }}>Male</button>
          <button onClick={() => setGender('female')} style={{ padding: '4px 12px', background: gender === 'female' ? '#2B6CB0' : '#E2E8F0', color: gender === 'female' ? '#FFF' : '#000', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Female</button>
        </div>
        <div>
          <span style={{ fontWeight: 'bold', marginRight: '8px' }}>View:</span>
          {(['front', 'back', 'left', 'right'] as ViewType[]).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              style={{
                padding: '4px 12px',
                marginRight: '4px',
                background: view === v ? '#2B6CB0' : '#E2E8F0',
                color: view === v ? '#FFF' : '#000',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                textTransform: 'capitalize',
              }}
            >
              {v}
            </button>
          ))}
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <span style={{ fontSize: '12px', color: '#4A5568' }}>Click on image to drop a pin</span>
        </div>
      </div>

      {/* Image container with click handler */}
      <div
        ref={imageContainerRef}
        style={{ position: 'relative', width: '100%', maxWidth: '500px', margin: '0 auto', cursor: 'crosshair' }}
        onClick={handleImageClick}
      >
        <img
          src={imageUrl}
          alt={`${gender} body – ${view} view`}
          useMap="#bodyMap"
          style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '8px', pointerEvents: 'none' }} // pointerEvents none so click goes to container
        />

        {/* Predefined region overlays (clickable) – we handle via area map */}
        <map name="bodyMap">
          {regions.map((region) => (
            <area
              key={region.id}
              shape={region.shape}
              coords={region.coords}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleRegionClick(region);
              }}
              style={{ cursor: 'pointer' }}
              title={region.label}
            />
          ))}
        </map>

        {/* Highlight overlay for selected predefined regions */}
        <svg
          viewBox="0 0 500 900"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
          }}
        >
          {regions.map((region) => {
            if (!isSelected(region.id)) return null;
            let d = '';
            if (region.shape === 'circle') {
              const [cx, cy, r] = region.coords.split(',').map(Number);
              const pts = [];
              for (let i = 0; i < 32; i++) {
                const angle = i * (2 * Math.PI) / 32;
                pts.push(`${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`);
              }
              d = 'M' + pts.join('L') + 'Z';
            } else if (region.shape === 'rect') {
              const [x, y, w, h] = region.coords.split(',').map(Number);
              d = `M${x},${y} L${x + w},${y} L${x + w},${y + h} L${x},${y + h} Z`;
            }
            return (
              <path
                key={region.id}
                d={d}
                fill="none"
                stroke="#F6AD55"
                strokeWidth="3"
                strokeDasharray="6,4"
                opacity="0.9"
              />
            );
          })}
        </svg>

        {/* Annotation pins */}
        {annotations
          .filter(a => a.view === view && a.gender === gender)
          .map((ann) => (
            <div
              key={ann.id}
              style={{
                position: 'absolute',
                left: `${ann.x}%`,
                top: `${ann.y}%`,
                transform: 'translate(-50%, -50%)',
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                backgroundColor: ann.severity === 'severe' ? '#EF4444' : ann.severity === 'moderate' ? '#F97316' : '#EAB308',
                border: '2px solid white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                cursor: 'pointer',
                pointerEvents: 'auto',
                zIndex: 10,
              }}
              onClick={(e) => {
                e.stopPropagation();
                // Show note in a tooltip or alert for now
                alert(`Note: ${ann.note}\nSeverity: ${ann.severity}\nFiles: ${ann.files?.length || 0} attached`);
              }}
              onDoubleClick={() => handleDeleteAnnotation(ann.id)}
              title="Double-click to delete"
            />
          ))}

        {/* Temporary pin placement (while adding) */}
        {isAddingAnnotation && tempAnnotation && (
          <div
            style={{
              position: 'absolute',
              left: `${tempAnnotation.x}%`,
              top: `${tempAnnotation.y}%`,
              transform: 'translate(-50%, -50%)',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor: '#38BDF8',
              border: '2px solid white',
              boxShadow: '0 0 0 4px rgba(56,189,248,0.4)',
              pointerEvents: 'none',
              zIndex: 20,
            }}
          />
        )}
      </div>

      {/* Annotation form (appears when adding a pin) */}
      {isAddingAnnotation && (
        <div style={{ marginTop: '16px', padding: '16px', background: '#FFF', borderRadius: '8px', border: '1px solid #CBD5E0' }}>
          <h4 style={{ margin: '0 0 12px 0' }}>Add Clinical Note</h4>
          <div style={{ marginBottom: '8px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>Severity</label>
            <select value={severity} onChange={(e) => setSeverity(e.target.value as any)} style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #CBD5E0' }}>
              <option value="mild">Mild</option>
              <option value="moderate">Moderate</option>
              <option value="severe">Severe</option>
            </select>
          </div>
          <div style={{ marginBottom: '8px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>Note</label>
            <VoiceTextarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={3} style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #CBD5E0', resize: 'vertical' }} placeholder="Describe the finding..." />
          </div>
          <div style={{ marginBottom: '8px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>Attach Files (photos/videos)</label>
            <input type="file" multiple accept="image/*,video/*" onChange={handleFileChange} ref={fileInputRef} style={{ width: '100%' }} />
            {files.length > 0 && <div style={{ fontSize: '12px', color: '#4A5568' }}>{files.length} file(s) selected</div>}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={handleSaveAnnotation} style={{ padding: '6px 16px', background: '#2B6CB0', color: '#FFF', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Save Pin</button>
            <button onClick={handleCancelAnnotation} style={{ padding: '6px 16px', background: '#E2E8F0', color: '#000', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Selected parts list */}
      <div style={{ marginTop: '12px', padding: '12px', background: '#FFF', borderRadius: '6px', border: '1px solid #E2E8F0' }}>
        <strong>Marked zones:</strong> {selectedParts.length > 0 ? selectedParts.map(p => p.name).join(', ') : 'None'}
        <span style={{ marginLeft: '16px' }}><strong>Pins:</strong> {annotations.filter(a => a.view === view && a.gender === gender).length}</span>
      </div>
    </div>
  );
};

export default BodyMap;